
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { CalendarIcon, CheckCircleIcon, ClubIcon as GymIcon } from "lucide-react"
import QRCode from "react-qr-code"
import { mockUserSubscriptions, mockSubscriptions, mockUsers } from "@/lib/mock-data"

export function UserMembership() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState("active")

  // Mock data for the current user
  const currentUserId = "1" // John Doe
  const user = mockUsers.find((u) => u.id === currentUserId)

  // Get user's active and past subscriptions
  const userSubscriptions = mockUserSubscriptions.filter((sub) => sub.userId === currentUserId)
  const activeSubscriptions = userSubscriptions.filter((sub) => sub.status === "active")
  const pastSubscriptions = userSubscriptions.filter((sub) => sub.status !== "active")

  // Generate QR code data
  const qrCodeData = JSON.stringify({
    userId: currentUserId,
    name: user?.name,
    subscriptionIds: activeSubscriptions.map((sub) => sub.subscriptionId),
    timestamp: new Date().toISOString(),
  })

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t("userView.myMembership.title")}</h1>

      {/* Active membership card with QR code */}
      {activeSubscriptions.length > 0 && (
        <Card className="border-green-500 dark:border-green-700">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>{t("userView.myMembership.activeMembership")}</CardTitle>
                <CardDescription>{t("userView.myMembership.scanToAccess")}</CardDescription>
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100">
                {t("userView.myMembership.active")}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center py-4">
            <div className="bg-white p-3 rounded-lg mb-4">
              <QRCode value={qrCodeData} size={200} />
            </div>

            {activeSubscriptions.map((subscription) => {
              const subDetails = mockSubscriptions.find((s) => s.id === subscription.subscriptionId)
              return (
                <div key={subscription.id} className="w-full space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="font-medium">{subDetails?.name}</div>
                    <div className="text-muted-foreground">${subDetails?.price}</div>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <CalendarIcon className="mr-1 h-4 w-4" />
                    <span>
                      {t("userView.myMembership.validUntil", {
                        date: new Date(subscription.endDate).toLocaleDateString(),
                      })}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <GymIcon className="mr-1 h-4 w-4" />
                    <span>{t("userView.myMembership.accessAllGyms")}</span>
                  </div>
                </div>
              )
            })}
          </CardContent>
          <CardFooter>
            <Button variant="outline" className="w-full">
              {t("userView.myMembership.renewMembership")}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Membership history */}
      <Card>
        <CardHeader>
          <CardTitle>{t("userView.myMembership.membershipHistory")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="active">{t("userView.myMembership.active")}</TabsTrigger>
              <TabsTrigger value="past">{t("userView.myMembership.past")}</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4 pt-4">
              {activeSubscriptions.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {t("userView.myMembership.noActiveMemberships")}
                </div>
              ) : (
                activeSubscriptions.map((subscription) => {
                  const subDetails = mockSubscriptions.find((s) => s.id === subscription.subscriptionId)
                  return (
                    <div key={subscription.id} className="flex items-center justify-between border-b pb-4">
                      <div>
                        <div className="font-medium">{subDetails?.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {t("userView.myMembership.startedOn", {
                            date: new Date(subscription.startDate).toLocaleDateString(),
                          })}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium">{t("userView.myMembership.active")}</span>
                      </div>
                    </div>
                  )
                })
              )}
            </TabsContent>

            <TabsContent value="past" className="space-y-4 pt-4">
              {pastSubscriptions.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {t("userView.myMembership.noPastMemberships")}
                </div>
              ) : (
                pastSubscriptions.map((subscription) => {
                  const subDetails = mockSubscriptions.find((s) => s.id === subscription.subscriptionId)
                  return (
                    <div key={subscription.id} className="flex items-center justify-between border-b pb-4">
                      <div>
                        <div className="font-medium">{subDetails?.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {t("userView.myMembership.expired", {
                            date: new Date(subscription.endDate).toLocaleDateString(),
                          })}
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        {t("userView.myMembership.renew")}
                      </Button>
                    </div>
                  )
                })
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
// Update references to subscriptions to use plans instead
// This is a placeholder update - in a real app, you would need to update the API calls
// and data structures to match the new plans model
